import { useRef, useState } from "react";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import { type HolidayEntry, type TierKey } from "@reach/core";
import type { AnalyticsData, KbGap, KbItem, QueryLogItem, Settings } from "../types";

export const DENSITY_STORAGE_KEY = "reach_dashboard_density";

type S<T> = Dispatch<SetStateAction<T>>;

export type BillingInfo = {
  subscriptionStatus: string;
  planEffectiveUntil: string | null;
  trialEndsAt: string | null;
  billingProvider: string | null;
  compedReason: string | null;
};

export type SettingsFormGroup = {
  formState: Settings;
  setFormState: S<Settings>;
  savedFormState: MutableRefObject<Settings>;
  isDirty: boolean;
  setIsDirty: S<boolean>;
  dirtySections: Set<string>;
  setDirtySections: S<Set<string>>;
  loading: boolean;
  setLoading: S<boolean>;
  density: "comfortable" | "compact";
  configLoaded: boolean;
  setConfigLoaded: S<boolean>;
};

export type SettingsKbGroup = {
  kbItems: KbItem[];
  setKbItems: S<KbItem[]>;
  kbGaps: KbGap[];
  setKbGaps: S<KbGap[]>;
  kbPage: number;
  setKbPage: S<number>;
  kbItemsPerPage: number;
  kbSearch: string;
  setKbSearch: S<string>;
  kbDeleteLoading: boolean;
  setKbDeleteLoading: S<boolean>;
  kbWipeConfirmText: string;
  setKbWipeConfirmText: S<string>;
  gapPage: number;
  setGapPage: S<number>;
  gapItemsPerPage: number;
  scrapeUrl: string;
  setScrapeUrl: S<string>;
  showSetupGuide: boolean;
  setShowSetupGuide: S<boolean>;
  selectedKbId: string | null;
  setSelectedKbId: S<string | null>;
  pendingDeleteId: string | null;
  setPendingDeleteId: S<string | null>;
};

export type SettingsTrainingGroup = {
  qQuestion: string;
  setQQuestion: S<string>;
  qCustomReply: string;
  setQCustomReply: S<string>;
  qSuggestions: string;
  setQSuggestions: S<string>;
  qKeywords: string;
  setQKeywords: S<string>;
  qSource: string;
  setQSource: S<string>;
  qTargetUrl: string;
  setQTargetUrl: S<string>;
  qContent: string;
  setQContent: S<string>;
  duplicateTopic: KbItem | null;
  setDuplicateTopic: S<KbItem | null>;
  isTrainingReadOnly: boolean;
  setIsTrainingReadOnly: S<boolean>;
  isTrainingModalOpen: boolean;
  setIsTrainingModalOpen: S<boolean>;
};

export type SettingsBillingGroup = {
  billingInfo: BillingInfo;
  setBillingInfo: S<BillingInfo>;
  billingCycle: "monthly" | "annual";
  setBillingCycle: S<"monthly" | "annual">;
  storeId: string | null;
  setStoreId: S<string | null>;
  pendingPlan: TierKey | null;
  setPendingPlan: S<TierKey | null>;
  pendingProviderTier: TierKey | null;
  setPendingProviderTier: S<TierKey | null>;
  selectedProvider: "stripe" | "lemonsqueezy" | null;
  setSelectedProvider: S<"stripe" | "lemonsqueezy" | null>;
  rememberProvider: boolean;
  setRememberProvider: S<boolean>;
  promoCode: string;
  setPromoCode: S<string>;
  showPromoField: boolean;
  setShowPromoField: S<boolean>;
  expandedFaq: number | null;
  setExpandedFaq: S<number | null>;
};

export type SettingsAnalyticsGroup = {
  analyticsData: AnalyticsData | null;
  setAnalyticsData: S<AnalyticsData | null>;
  analyticsPeriod: string;
  setAnalyticsPeriod: S<string>;
  analyticsLoading: boolean;
  setAnalyticsLoading: S<boolean>;
  analyticsWipeLoading: boolean;
  setAnalyticsWipeLoading: S<boolean>;
  isRefreshing: boolean;
  setIsRefreshing: S<boolean>;
  queryLog: QueryLogItem[];
  setQueryLog: S<QueryLogItem[]>;
  queryLogLoading: boolean;
  setQueryLogLoading: S<boolean>;
  chatbotSectionOpen: boolean;
  setChatbotSectionOpen: S<boolean>;
  visitorSectionOpen: boolean;
  setVisitorSectionOpen: S<boolean>;
  wipeConfirmText: string;
  setWipeConfirmText: S<string>;
};

export type SettingsModalsGroup = {
  isModalOpen: boolean;
  setIsModalOpen: S<boolean>;
  showWipeModal: boolean;
  setShowWipeModal: S<boolean>;
  showAnalyticsWipeModal: boolean;
  setShowAnalyticsWipeModal: S<boolean>;
  showDeleteTopicModal: boolean;
  setShowDeleteTopicModal: S<boolean>;
  showProviderModal: boolean;
  setShowProviderModal: S<boolean>;
  showHolidayModal: boolean;
  setShowHolidayModal: S<boolean>;
  showImportResultsModal: boolean;
  setShowImportResultsModal: S<boolean>;
};

export type SettingsHolidayGroup = {
  localHolidays: (string | HolidayEntry)[];
  setLocalHolidays: S<(string | HolidayEntry)[]>;
  editingHolidayId: string | null;
  setEditingHolidayId: S<string | null>;
  hName: string;
  setHName: S<string>;
  month: number;
  year: number;
  setMonth: S<{ month: number; year: number }>;
  selectedDates: { start: Date; end: Date };
  setSelectedDates: S<{ start: Date; end: Date }>;
};

export type SettingsUiGroup = {
  selectedTab: number;
  setSelectedTab: S<number>;
  toastActive: boolean;
  setToastActive: S<boolean>;
  toastMsg: string;
  setToastMsg: S<string>;
  showOnboarding: boolean;
  setShowOnboarding: S<boolean>;
  importResults: {
    inserted: string[];
    updated: string[];
    skipped: number;
    total: number;
  };
  setImportResults: S<{
    inserted: string[];
    updated: string[];
    skipped: number;
    total: number;
  }>;
};

export function useSettingsPageState(shopDomain: string, defaultSettings: Settings) {
  const [formState, setFormState] = useState<Settings>(defaultSettings);
  const savedFormState = useRef<Settings>(defaultSettings);
  const [selectedTab, setSelectedTab] = useState<number>(() => {
    try {
      return parseInt(sessionStorage.getItem("reach_selected_tab") || "0") || 0;
    } catch {
      return 0;
    }
  });
  const [density] = useState<"comfortable" | "compact">(() => {
    try {
      return localStorage.getItem(DENSITY_STORAGE_KEY) === "compact" ? "compact" : "comfortable";
    } catch {
      return "comfortable";
    }
  });
  const [isDirty, setIsDirty] = useState(false);
  const [dirtySections, setDirtySections] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [storeId, setStoreId] = useState<string | null>(null);
  const [toastActive, setToastActive] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [configLoaded, setConfigLoaded] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem(`reach_onboarded_${shopDomain}`));

  const [kbItems, setKbItems] = useState<KbItem[]>([]);
  const [kbGaps, setKbGaps] = useState<KbGap[]>([]);
  const [showSetupGuide, setShowSetupGuide] = useState(true);
  const [qQuestion, setQQuestion] = useState("");
  const [qCustomReply, setQCustomReply] = useState("");
  const [qSuggestions, setQSuggestions] = useState("");
  const [qKeywords, setQKeywords] = useState("");
  const [qSource, setQSource] = useState("manual");
  const [qTargetUrl, setQTargetUrl] = useState("");
  const [qContent, setQContent] = useState("");
  const [duplicateTopic, setDuplicateTopic] = useState<KbItem | null>(null);
  const [isTrainingReadOnly, setIsTrainingReadOnly] = useState(false);
  const [kbPage, setKbPage] = useState(1);
  const [kbItemsPerPage] = useState(25);
  const [gapPage, setGapPage] = useState(1);
  const [gapItemsPerPage] = useState(5);
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [selectedKbId, setSelectedKbId] = useState<string | null>(null);
  const [kbSearch, setKbSearch] = useState("");
  const [isTrainingModalOpen, setIsTrainingModalOpen] = useState(false);
  const [kbDeleteLoading, setKbDeleteLoading] = useState(false);
  const [kbWipeConfirmText, setKbWipeConfirmText] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showWipeModal, setShowWipeModal] = useState(false);
  const [showAnalyticsWipeModal, setShowAnalyticsWipeModal] = useState(false);
  const [showDeleteTopicModal, setShowDeleteTopicModal] = useState(false);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [showImportResultsModal, setShowImportResultsModal] = useState(false);

  const [billingInfo, setBillingInfo] = useState<BillingInfo>({
    subscriptionStatus: "active",
    planEffectiveUntil: null,
    trialEndsAt: null,
    billingProvider: null,
    compedReason: null,
  });
  const [pendingPlan, setPendingPlan] = useState<TierKey | null>(null);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");
  const [pendingProviderTier, setPendingProviderTier] = useState<TierKey | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<"stripe" | "lemonsqueezy" | null>(null);
  const [rememberProvider, setRememberProvider] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [showPromoField, setShowPromoField] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState("30");
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsWipeLoading, setAnalyticsWipeLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [queryLog, setQueryLog] = useState<QueryLogItem[]>([]);
  const [queryLogLoading, setQueryLogLoading] = useState(false);
  const [chatbotSectionOpen, setChatbotSectionOpen] = useState(false);
  const [visitorSectionOpen, setVisitorSectionOpen] = useState(false);
  const [wipeConfirmText, setWipeConfirmText] = useState("");
  const [importResults, setImportResults] = useState({
    inserted: [] as string[],
    updated: [] as string[],
    skipped: 0,
    total: 0,
  });

  const [localHolidays, setLocalHolidays] = useState<(string | HolidayEntry)[]>([]);
  const [editingHolidayId, setEditingHolidayId] = useState<string | null>(null);
  const [hName, setHName] = useState("");
  const [{ month, year }, setMonth] = useState({ month: new Date().getMonth(), year: new Date().getFullYear() });
  const [selectedDates, setSelectedDates] = useState({ start: new Date(), end: new Date() });

  const form: SettingsFormGroup = {
    formState, setFormState, savedFormState,
    isDirty, setIsDirty,
    dirtySections, setDirtySections,
    loading, setLoading,
    density,
    configLoaded, setConfigLoaded,
  };

  const kb: SettingsKbGroup = {
    kbItems, setKbItems,
    kbGaps, setKbGaps,
    kbPage, setKbPage, kbItemsPerPage,
    kbSearch, setKbSearch,
    kbDeleteLoading, setKbDeleteLoading,
    kbWipeConfirmText, setKbWipeConfirmText,
    gapPage, setGapPage, gapItemsPerPage,
    scrapeUrl, setScrapeUrl,
    showSetupGuide, setShowSetupGuide,
    selectedKbId, setSelectedKbId,
    pendingDeleteId, setPendingDeleteId,
  };

  const training: SettingsTrainingGroup = {
    qQuestion, setQQuestion,
    qCustomReply, setQCustomReply,
    qSuggestions, setQSuggestions,
    qKeywords, setQKeywords,
    qSource, setQSource,
    qTargetUrl, setQTargetUrl,
    qContent, setQContent,
    duplicateTopic, setDuplicateTopic,
    isTrainingReadOnly, setIsTrainingReadOnly,
    isTrainingModalOpen, setIsTrainingModalOpen,
  };

  const billing: SettingsBillingGroup = {
    billingInfo, setBillingInfo,
    billingCycle, setBillingCycle,
    storeId, setStoreId,
    pendingPlan, setPendingPlan,
    pendingProviderTier, setPendingProviderTier,
    selectedProvider, setSelectedProvider,
    rememberProvider, setRememberProvider,
    promoCode, setPromoCode,
    showPromoField, setShowPromoField,
    expandedFaq, setExpandedFaq,
  };

  const analytics: SettingsAnalyticsGroup = {
    analyticsData, setAnalyticsData,
    analyticsPeriod, setAnalyticsPeriod,
    analyticsLoading, setAnalyticsLoading,
    analyticsWipeLoading, setAnalyticsWipeLoading,
    isRefreshing, setIsRefreshing,
    queryLog, setQueryLog,
    queryLogLoading, setQueryLogLoading,
    chatbotSectionOpen, setChatbotSectionOpen,
    visitorSectionOpen, setVisitorSectionOpen,
    wipeConfirmText, setWipeConfirmText,
  };

  const modals: SettingsModalsGroup = {
    isModalOpen, setIsModalOpen,
    showWipeModal, setShowWipeModal,
    showAnalyticsWipeModal, setShowAnalyticsWipeModal,
    showDeleteTopicModal, setShowDeleteTopicModal,
    showProviderModal, setShowProviderModal,
    showHolidayModal, setShowHolidayModal,
    showImportResultsModal, setShowImportResultsModal,
  };

  const holiday: SettingsHolidayGroup = {
    localHolidays, setLocalHolidays,
    editingHolidayId, setEditingHolidayId,
    hName, setHName,
    month, year, setMonth,
    selectedDates, setSelectedDates,
  };

  const ui: SettingsUiGroup = {
    selectedTab, setSelectedTab,
    toastActive, setToastActive,
    toastMsg, setToastMsg,
    showOnboarding, setShowOnboarding,
    importResults, setImportResults,
  };

  return { form, kb, training, billing, analytics, modals, holiday, ui };
}
